import { useGetDcaHoldingsQuery } from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { PnLPill } from "@/components/domain/PnLPill";
import { fmtUsd } from "@/lib/format";

/** DCA cost-basis summary — one row per symbol with avg buy price,
 *  current price, and unrealized P&L. Reused on Overview.
 *
 *  Admin: aggregates across every user's DCA fills.
 *  User:  only their own.
 */
export function DcaHoldings({ pollMs = 30000 }: { pollMs?: number }) {
  const q = useGetDcaHoldingsQuery(undefined, { pollingInterval: pollMs });
  const data = q.data;

  if (q.isLoading) {
    return (
      <Card>
        <CardHeader><CardTitle>DCA Holdings</CardTitle></CardHeader>
        <CardContent><Skeleton className="h-24 w-full" /></CardContent>
      </Card>
    );
  }
  if (!data || data.holdings.length === 0) {
    // Hide silently when no DCA buys exist — keeps the Overview tidy.
    return null;
  }

  const t = data.totals;

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-2 flex-wrap">
        <CardTitle className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span>DCA Holdings</span>
          <span className="text-xs text-muted font-normal">
            cost {fmtUsd(t.cost_usd)} · value {fmtUsd(t.value_usd)}
          </span>
          <span className="text-xs text-muted">unrealized:</span>
          <PnLPill value={t.unrealized_usd} />
          <span className={`text-xs font-semibold ${
            t.unrealized_pct >= 0 ? "text-green-500" : "text-red-500"
          }`}>
            {t.unrealized_pct >= 0 ? "+" : ""}{t.unrealized_pct.toFixed(2)}%
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* P&L summary strip — realized, unrealized, total, compound pool */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 text-sm">
          <div className="border rounded p-2">
            <div className="text-xs text-muted">Unrealized P&L</div>
            <div className={`text-lg font-semibold tabular-nums ${
              t.unrealized_usd >= 0 ? "text-green-500" : "text-red-500"
            }`}>
              {t.unrealized_usd >= 0 ? "+" : ""}{fmtUsd(t.unrealized_usd)}
            </div>
            <div className="text-[10px] text-muted">live, on remaining position</div>
          </div>
          <div className="border rounded p-2">
            <div className="text-xs text-muted">Realized P&L</div>
            <div className={`text-lg font-semibold tabular-nums ${
              t.realized_usd >= 0 ? "text-green-500" : "text-red-500"
            }`}>
              {t.realized_usd >= 0 ? "+" : ""}{fmtUsd(t.realized_usd)}
            </div>
            <div className="text-[10px] text-muted">from take-profit fires + sells</div>
          </div>
          <div className="border rounded p-2">
            <div className="text-xs text-muted">Total P&L</div>
            <div className={`text-lg font-semibold tabular-nums ${
              t.total_pnl_usd >= 0 ? "text-green-500" : "text-red-500"
            }`}>
              {t.total_pnl_usd >= 0 ? "+" : ""}{fmtUsd(t.total_pnl_usd)}
            </div>
            <div className="text-[10px] text-muted">realized + unrealized</div>
          </div>
          <div className="border rounded p-2">
            <div className="text-xs text-muted">Compound pool</div>
            <div className="text-lg font-semibold tabular-nums text-blue-500">
              {fmtUsd(t.compound_pool_usd)}
            </div>
            <div className="text-[10px] text-muted">waiting for smart-DCA redeploy</div>
          </div>
        </div>

        <Table>
          <THead>
            <TR>
              <TH>Symbol</TH>
              <TH className="text-right">Qty</TH>
              <TH className="text-right">Avg buy</TH>
              <TH className="text-right">Current</TH>
              <TH className="text-right">Cost</TH>
              <TH className="text-right">Value</TH>
              <TH className="text-right">Δ%</TH>
              <TH className="text-right">Unrealized</TH>
              <TH className="text-right">Realized</TH>
            </TR>
          </THead>
          <TBody>
            {data.holdings.map((h) => {
              const pct = h.unrealized_pct;
              const dollar = h.unrealized_usd;
              const realized = h.realized_usd ?? 0;
              const value = h.current_price != null ? h.qty * h.current_price : null;
              const pctClass = pct == null
                ? "text-muted"
                : pct >= 0
                  ? "text-green-500"
                  : "text-red-500";
              return (
                <TR key={h.symbol}>
                  <TD className="font-semibold">{h.symbol}</TD>
                  <TD className="text-right tabular-nums">{h.qty.toFixed(6)}</TD>
                  <TD className="text-right tabular-nums">
                    {h.avg_price.toFixed(h.avg_price >= 100 ? 2 : 6)}
                  </TD>
                  <TD className="text-right tabular-nums">
                    {h.current_price != null
                      ? h.current_price.toFixed(h.current_price >= 100 ? 2 : 6)
                      : <span className="text-muted">—</span>}
                  </TD>
                  <TD className="text-right tabular-nums">{fmtUsd(h.cost_usd)}</TD>
                  <TD className="text-right tabular-nums">
                    {value != null ? fmtUsd(value) : <span className="text-muted">—</span>}
                  </TD>
                  <TD className={`text-right font-semibold tabular-nums ${pctClass}`}>
                    {pct == null
                      ? "—"
                      : `${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`}
                  </TD>
                  <TD className="text-right">
                    {dollar != null ? <PnLPill value={dollar} /> : <span className="text-muted">—</span>}
                  </TD>
                  <TD className={`text-right tabular-nums ${
                    realized > 0 ? "text-green-500 font-semibold"
                    : realized < 0 ? "text-red-500"
                    : "text-muted"
                  }`}>
                    {realized === 0 ? "—" : `${realized >= 0 ? "+" : ""}${fmtUsd(realized)}`}
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      </CardContent>
    </Card>
  );
}
